import {
  DETAILS_SECTION_ATTRIBUTE,
  DETAILS_SECTION_HEADER_ATTRIBUTE,
  PENDENCY_KEY_ATTRIBUTE,
  PENDENCY_TARGET_MIN_VISIBLE_PX,
  SECTION_PENDENCY_ALERT_ATTRIBUTE,
} from '../../objectScreen.constants';

export type PendencyDirection = 'up' | 'down';

export type PendencyPosition = 'above' | 'inside' | 'below';

export interface PendencyBounds {
  top: number;
  bottom: number;
}

export interface ResolvePendencyPositionParams {
  bounds: PendencyBounds;
  rootBounds: PendencyBounds;
}

export interface PendencyPlacement<Target> {
  target: Target;
  bounds: PendencyBounds;
}

export interface OffscreenPendency<Target> {
  target: Target;
  direction: PendencyDirection;
}

export interface PendencyCandidate<Target> {
  node: Target | null;
  isReachable: boolean;
  sectionHeader: Target | null;
  isSectionHeaderReachable: boolean;
}

export const resolvePendencyPosition = ({
  bounds,
  rootBounds,
}: ResolvePendencyPositionParams): PendencyPosition => {
  const requiredOverlap = Math.min(
    PENDENCY_TARGET_MIN_VISIBLE_PX,
    bounds.bottom - bounds.top,
    rootBounds.bottom - rootBounds.top,
  );

  if (bounds.bottom - rootBounds.top < requiredOverlap) {
    return 'above';
  }

  if (rootBounds.bottom - bounds.top < requiredOverlap) {
    return 'below';
  }

  return 'inside';
};

export const findFirstOffscreenPendency = <Target>(
  placements: PendencyPlacement<Target>[],
  rootBounds: PendencyBounds,
): OffscreenPendency<Target> | null => {
  for (const placement of placements) {
    const position = resolvePendencyPosition({ bounds: placement.bounds, rootBounds });

    if (position !== 'inside') {
      return {
        target: placement.target,
        direction: position === 'above' ? 'up' : 'down',
      };
    }
  }

  return null;
};

const resolveCandidateTarget = <Target>(
  candidate: PendencyCandidate<Target>,
  representedSections: Set<Target>,
): Target | null => {
  if (candidate.node !== null && candidate.isReachable) {
    return candidate.node;
  }

  const { sectionHeader } = candidate;

  if (
    !sectionHeader ||
    !candidate.isSectionHeaderReachable ||
    representedSections.has(sectionHeader)
  ) {
    return null;
  }

  return sectionHeader;
};

const collectSectionsWithReachableRow = <Target>(candidates: PendencyCandidate<Target>[]) =>
  new Set(
    candidates.flatMap<Target>((candidate) =>
      candidate.node !== null && candidate.isReachable && candidate.sectionHeader !== null
        ? [candidate.sectionHeader]
        : [],
    ),
  );

export const resolveNavigableTargets = <Target>(
  candidates: PendencyCandidate<Target>[],
): Target[] => {
  const targets: Target[] = [];
  const representedSections = collectSectionsWithReachableRow(candidates);

  for (const candidate of candidates) {
    const target = resolveCandidateTarget(candidate, representedSections);

    if (target === null) {
      continue;
    }

    if (candidate.sectionHeader) {
      representedSections.add(candidate.sectionHeader);
    }

    targets.push(target);
  }

  return targets;
};

const isElementReachable = (element: HTMLElement) =>
  element.isConnected &&
  element.offsetParent !== null &&
  getComputedStyle(element).visibility !== 'hidden';

const resolveSectionHeader = (node: HTMLElement) => {
  const section = node.closest<HTMLElement>(`[${DETAILS_SECTION_ATTRIBUTE}]`);

  return (
    section?.querySelector<HTMLElement>(`:scope > [${DETAILS_SECTION_HEADER_ATTRIBUTE}]`) ?? null
  );
};

const compareDocumentOrder = (first: HTMLElement, second: HTMLElement) =>
  (first.compareDocumentPosition(second) & Node.DOCUMENT_POSITION_FOLLOWING) === 0 ? 1 : -1;

const collectRowCandidates = (root: HTMLElement, pendencyKeys: Set<string>) => {
  const nodes = Array.from(root.querySelectorAll<HTMLElement>(`[${PENDENCY_KEY_ATTRIBUTE}]`));

  return nodes.flatMap<PendencyCandidate<HTMLElement>>((node) => {
    const key = node.getAttribute(PENDENCY_KEY_ATTRIBUTE);

    if (key === null || !pendencyKeys.has(key)) {
      return [];
    }

    const sectionHeader = resolveSectionHeader(node);

    return [
      {
        node,
        sectionHeader,
        isReachable: isElementReachable(node),
        isSectionHeaderReachable: sectionHeader !== null && isElementReachable(sectionHeader),
      },
    ];
  });
};

const collectAlertedSectionCandidates = (root: HTMLElement) => {
  const alerts = Array.from(
    root.querySelectorAll<HTMLElement>(`[${SECTION_PENDENCY_ALERT_ATTRIBUTE}]`),
  );

  return alerts.flatMap<PendencyCandidate<HTMLElement>>((alert) => {
    const sectionHeader = alert.closest<HTMLElement>(`[${DETAILS_SECTION_HEADER_ATTRIBUTE}]`);

    if (sectionHeader === null) {
      return [];
    }

    return [
      {
        node: null,
        sectionHeader,
        isReachable: false,
        isSectionHeaderReachable: isElementReachable(sectionHeader),
      },
    ];
  });
};

export const collectPendencyTargets = (root: HTMLElement, pendencyKeys: Set<string>) =>
  resolveNavigableTargets([
    ...collectRowCandidates(root, pendencyKeys),
    ...collectAlertedSectionCandidates(root),
  ]).sort(compareDocumentOrder);
